import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ToFixedValue from '../../../../../shared-functions/ToFixedValue';

interface Segment {
  startsAt: string;
  endsAt: string;
  status: string;
}

interface DataProperties {
  date: string; // e.g. "2025-11-22"
  times: Segment[];
}

interface UptimeChartProperties {
  data: DataProperties[];
  todayUptime: any;
}

/**
 * Helper: given a segment and a day (YYYY-MM-DD),
 * return the [startPercent, endPercent] **clamped to that day**.
 * If the segment does not touch that day → return null.
 * @param {any} segment - The segment to check
 * @param {string} dayDate - The date of the day
 * @returns {any} The start and end percentages
 */
const getSegmentPercentRangeForDay = (segment: Segment, dayDate: string) => {
  // Treat day boundaries in UTC to match ISO "Z" timestamps
  const dayStart = new Date(`${dayDate}T00:00:00.000Z`);
  const dayEnd = new Date(`${dayDate}T23:59:59.999Z`);

  const segStart = new Date(segment.startsAt);
  const segEnd = new Date(segment.endsAt);

  // Clamp segment inside [dayStart, dayEnd]
  const clampedStart = segStart < dayStart ? dayStart : segStart;
  const clampedEnd = segEnd > dayEnd ? dayEnd : segEnd;

  // No overlap with this day
  if (clampedEnd <= clampedStart) return null;

  // Use same reference (UTC) for minutes
  const startMinutes = clampedStart.getUTCHours() * 60 + clampedStart.getUTCMinutes();
  const endMinutes = clampedEnd.getUTCHours() * 60 + clampedEnd.getUTCMinutes();

  const startPercent = (startMinutes / 1440) * 100;
  const endPercent = (endMinutes / 1440) * 100;

  return { startPercent, endPercent };
};

/**
 * Function for getting time position
 * @param {string} time The time string
 * @returns {number} The percentage position
 */
const getTimePosition = (time: string) => {
  const [timePart, period] = time.trim().split(' ');
  const [hoursString, minsString] = timePart.split(':');
  let hours = Number(hoursString);
  const mins = Number(minsString);

  if (period.toLowerCase() === 'pm' && hours !== 12) hours += 12;
  if (period.toLowerCase() === 'am' && hours === 12) hours = 0;

  const totalMinutes = hours * 60 + mins;
  return (totalMinutes / 1440) * 100;
};

/**
 * Function for position to time
 * @param {number} pos The percentage position
 * @returns {string} The time string
 */
const positionToTime = (pos: number) => {
  const totalMinutes = (pos / 100) * 1440;
  const hours = Math.floor(totalMinutes / 60);
  const mins = Math.floor(totalMinutes % 60);

  const ampm = hours >= 12 ? 'PM' : 'AM';
  let display = hours % 12;
  if (display === 0) display = 12;

  return `${String(display).padStart(2, '0')}:${String(mins).padStart(2, '0')} ${ampm}`;
};

/**
 * Uptime chart component
 * @param {any} properties - The component properties
 * @param {any} properties.data - The data for the chart
 * @param {any} properties.todayUptime - Today's uptime
 * @returns {any} The rendered component
 */
function UptimeChart({ data, todayUptime }: UptimeChartProperties) {
  const { t } = useTranslation('sidePanel');
  const [currentPos, setCurrentPos] = useState(() => {
    const now = new Date();
    let hours = now.getHours();
    const mins = now.getMinutes();

    // convert to 12-hour format with AM/PM
    const ampm = hours >= 12 ? 'PM' : 'AM';
    if (hours === 0) hours = 12;
    else if (hours > 12) hours -= 12;

    const currentTimeString = `${hours.toString().padStart(2, '0')}:${mins
      .toString()
      .padStart(2, '0')} ${ampm}`;

    return getTimePosition(currentTimeString);
  });
  const [isDragging, setIsDragging] = useState(false);
  const [tooltip, setTooltip] = useState<{ show: boolean; text: string; x: number; y: number }>({
    show: false,
    text: '',
    x: 0,
    y: 0,
  });
  const scrollReference = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging || !scrollReference.current) return;

      const rect = scrollReference.current.getBoundingClientRect();
      const labelWidth = 96; // left padding for labels
      const chartWidth = rect.width - labelWidth;

      // calculate x relative to the chart content (excluding labels)
      let x = event.clientX - rect.left - labelWidth;

      // clamp x so arrow tip stays inside chart
      const minX = 0; // start of chart
      const maxX = chartWidth; // end of chart
      x = Math.max(minX, Math.min(x, maxX));

      // convert to percentage and adjust for arrow width
      const pos = (x / chartWidth) * 100;
      setCurrentPos(pos);
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }

    return () => {};
  }, [isDragging, handleMouseMove, handleMouseUp]);

  /**
   * Get uptime/downtime until the current cursor position,
   * but **only inside the given day**.
   */
  const getStatsUntil = useCallback(
    (segments: Segment[], dayDate: string) => {
      let uptime = 0;
      let downtime = 0;

      segments.forEach((seg) => {
        const range = getSegmentPercentRangeForDay(seg, dayDate);
        if (!range) return;

        const { startPercent, endPercent } = range;

        if (startPercent < currentPos) {
          const adjustedEnd = Math.min(endPercent, currentPos);
          const duration = adjustedEnd - startPercent;

          if (duration > 0) {
            if (seg.status === 'online') {
              uptime += duration;
            } else {
              downtime += duration;
            }
          }
        }
      });

      return { uptime, downtime };
    },
    [currentPos],
  );

  /**
   * Function for mouse enter
   * @param {any} day - The day data
   * @param {any} event - The mouse event
   */
  const handleMouseMoveTooltip = (day: DataProperties, event: React.MouseEvent) => {
    const stats = getStatsUntil(day.times, day.date);

    const upMin = (stats.uptime / 100) * 1440;
    const downMin = (stats.downtime / 100) * 1440;

    const upHr = Math.floor(upMin / 60);
    const upM = Math.round(upMin % 60);

    const downHr = Math.floor(downMin / 60);
    const downM = Math.round(downMin % 60);

    let message = '';

    if (upMin === 0 && downMin === 0) {
      message = t('noDataTill', { time: positionToTime(currentPos) });
    } else if (downMin === 0) {
      message = `${t('uptimeTill', {
        time: positionToTime(currentPos),
      })}: <span class="uptime-text">${upHr}${t('h')} ${upM}${t('m')}</span>`;
    } else if (upMin === 0) {
      message = `${t('downtimeTill', {
        time: positionToTime(currentPos),
      })}: <span class="downtime-text">${downHr}${t('h')} ${downM}${t('m')}</span>`;
    } else {
      message = `${t('till', { time: positionToTime(currentPos) })} → <span class="uptime-text">${t(
        'uptime',
      )}: ${upHr}${t('h')} ${upM}${t('m')}</span>, <span class="downtime-text">${t(
        'downtime',
      )}: ${downHr}${t('h')} ${downM}${t('m')}</span>`;
    }

    if (scrollReference.current) {
      const rect = scrollReference.current.getBoundingClientRect();
      const tooltipW = 200;
      const tooltipH = 45;

      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const tooltipX = Math.min(Math.max(8, mouseX + 12), rect.width - tooltipW - 8);

      let tooltipY = mouseY - tooltipH - 12;
      if (tooltipY < 0) tooltipY = mouseY + 12;

      setTooltip({
        show: true,
        text: message,
        x: tooltipX,
        y: tooltipY,
      });
    }
  };

  /**
   * Function for mouse leave
   */
  const handleMouseLeave = () => setTooltip((previous) => ({ ...previous, show: false }));

  const timeSlots = Array.from({ length: 25 }, (_, index) => {
    const hour = index % 24;

    let display;
    if (hour === 0) display = 12;
    else if (hour > 12) display = hour - 12;
    else display = hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${display.toString().padStart(2, '0')}:00 ${ampm}`;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      let hours = now.getHours();
      const mins = now.getMinutes();

      const ampm = hours >= 12 ? 'PM' : 'AM';
      if (hours === 0) hours = 12;
      else if (hours > 12) hours -= 12;

      const currentTimeString = `${hours.toString().padStart(2, '0')}:${mins
        .toString()
        .padStart(2, '0')} ${ampm}`;

      setCurrentPos(getTimePosition(currentTimeString));
    }, 60_000); // update every minute

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="uptime-wrapper">
      <div className="chart-card">
        <div className="header">
          <div className="uptime-box">
            <span className="uptime-hours">{`${ToFixedValue(todayUptime)}${t('hrs')}`}</span>
            <span className="uptime-label">{t('todayUptime')}</span>
          </div>
        </div>

        <div className="chart-container">
          <div className="days-container">
            <div className="chart-content" ref={scrollReference}>
              {data.map((day) => (
                <div
                  key={day.date}
                  className="day-row"
                  onMouseMove={(event) => handleMouseMoveTooltip(day, event)}
                  onMouseLeave={handleMouseLeave}>
                  <div className="date-label">{day.date}</div>
                  <div className="bar-container">
                    {day.times.map((seg) => {
                      const range = getSegmentPercentRangeForDay(seg, day.date);
                      if (!range) return null;

                      const { startPercent, endPercent } = range;

                      return (
                        <div
                          key={seg.startsAt + seg.endsAt}
                          className={`segment ${seg.status}`}
                          style={{
                            left: `${startPercent}%`,
                            width: `${endPercent - startPercent}%`,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}

              <div
                role="presentation"
                className="current-time-line"
                style={{
                  left: `calc(96px + ${currentPos}%)`,
                  right: `calc(96px + ${currentPos}%)`,
                }}
                onMouseDown={() => setIsDragging(true)}>
                <div className="arrow-wrapper">
                  <div className="arrow" />
                </div>
              </div>

              <div className="time-labels">
                {timeSlots.map((time) => (
                  <div key={time} className="time-label">
                    {time}
                  </div>
                ))}
              </div>
              {/* ✅ Tooltip now inside chart */}
              {tooltip.show && (
                <div
                  className="tooltip"
                  style={{ top: tooltip.y, left: tooltip.x }}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: tooltip.text }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UptimeChart;
